import { FunctionComponent } from "react";

import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { Checkbox } from "@/components/Checkbox/Checkbox";

import { BlacklistOverrideVerdict } from "@/pages/Private/pages/Blacklist/schema/blacklist-override";

import {
	BlacklistOverridePlan,
	BlacklistOverrideWording,
	canConfirmOverride,
	formatCompanyNames,
	REFUSAL_LABEL_KEYS,
	WORDING_KEYS,
} from "./blacklistOverride";

/**
 * Above the Reach-out menu (z 1190/1200), the lead panel (z 150, or 1500 in the extension) and the
 * confirm modals of the list table, any of which can be what opened it.
 */
export const BLACKLIST_OVERRIDE_Z_INDEX = 1600;

interface BlacklistOverrideModalProps {
	plan: BlacklistOverridePlan;
	wording: BlacklistOverrideWording;
	removeCompany: boolean;
	/** The confirmed override is being applied; nothing can be decided again until it lands. */
	busy: boolean;
	/** A line about the action's scope, e.g. a select-all that only goes out for the loaded page. */
	note?: string;
	onToggleRemoveCompany: () => void;
	onConfirm: () => void;
	onCancel: () => void;
}

/**
 * Asks before a blacklisted lead is contacted or taken off the blacklist (PREC-2548). It lists who
 * will come off, who stays on and why, asks for the company where a whole company blocks a lead,
 * and says that a competitor stays a competitor. With nothing it may take off, it only explains:
 * there is no confirm button for a decision that cannot be made.
 */
export const BlacklistOverrideModal: FunctionComponent<BlacklistOverrideModalProps> = ({
	plan,
	wording,
	removeCompany,
	busy,
	note,
	onToggleRemoveCompany,
	onConfirm,
	onCancel,
}) => {
	const { t } = useTranslation();
	const count = plan.overridable.length + plan.refused.length;
	const keys = WORDING_KEYS[wording];
	const canTakeAnyOff = plan.overridable.length > 0;

	const renderLead = (verdict: BlacklistOverrideVerdict, reason?: string) => (
		<li
			key={verdict.listPersonId}
			className={
				reason
					? "rounded-lg border border-status-error bg-status-error-light px-3 py-2"
					: "rounded-lg border border-border-light px-3 py-2"
			}
		>
			<span className="font-semibold text-text">{verdict.name}</span>
			{verdict.companyName && <span className="text-text-meta"> · {verdict.companyName}</span>}
			{reason && <span className="block text-xs text-status-error">{reason}</span>}
		</li>
	);

	return (
		<Modal
			isOpened
			cancelButtonText={canTakeAnyOff ? undefined : t("basics.close")}
			disableSave={!canConfirmOverride(plan, removeCompany)}
			handleClose={busy ? undefined : onCancel}
			handleSave={canTakeAnyOff ? onConfirm : undefined}
			isLoading={busy}
			size="sm"
			submitButtonText={t(keys.confirm)}
			title={t(keys.title, { count })}
			zIndex={BLACKLIST_OVERRIDE_Z_INDEX}
		>
			<div className="flex flex-col gap-3 text-sm" data-testid="blacklist-override-modal">
				<p className="text-text-meta">{t(keys.intro)}</p>

				{canTakeAnyOff && (
					<section className="flex flex-col gap-1.5" data-testid="blacklist-override-overridable">
						<h4 className="text-xs font-semibold text-text-meta">
							{t("blacklist.override.overridableHeading")}
						</h4>
						<ul className="flex max-h-[200px] flex-col gap-1.5 overflow-y-auto">
							{plan.overridable.map((verdict) => renderLead(verdict))}
						</ul>
					</section>
				)}

				{plan.refused.length > 0 && (
					<section className="flex flex-col gap-1.5" data-testid="blacklist-override-refused">
						<h4 className="text-xs font-semibold text-text-meta">
							{t("blacklist.override.refusedHeading")}
						</h4>
						<ul className="flex max-h-[200px] flex-col gap-1.5 overflow-y-auto">
							{plan.refused.map((verdict) =>
								renderLead(verdict, verdict.refusal ? t(REFUSAL_LABEL_KEYS[verdict.refusal]) : "")
							)}
						</ul>
					</section>
				)}

				{!canTakeAnyOff && (
					<p className="font-semibold text-text">{t("blacklist.override.nothingOverridable")}</p>
				)}

				{plan.needsCompanyConsent && (
					<div className="flex flex-col gap-1 rounded-lg border border-border-light px-3 py-2">
						<Checkbox
							isChecked={removeCompany}
							isDisabled={busy}
							testId="blacklist-override-remove-company"
							onChange={onToggleRemoveCompany}
						>
							{t("blacklist.override.removeCompany", {
								companies: formatCompanyNames(plan.companiesToRemove, t),
							})}
						</Checkbox>
						<p className="pl-7 text-xs text-text-meta">
							{t("blacklist.override.removeCompanyHint")}
						</p>
					</div>
				)}

				{plan.competitorCount > 0 && (
					<p
						className="rounded-lg bg-status-warning-light px-3 py-2 text-xs text-status-warning"
						data-testid="blacklist-override-competitor-note"
					>
						{t("blacklist.override.competitorNote", {
							count: plan.competitorCount,
							companies: formatCompanyNames(plan.competitorCompanies, t),
						})}
					</p>
				)}

				{note && <p className="text-xs text-text-meta">{note}</p>}
			</div>
		</Modal>
	);
};
